import { Helmet } from 'react-helmet-async';
import { sentenceCase } from 'change-case';
import { useContext, useState } from 'react';
// @mui
import {
  Box,
  Card,
  Table,
  Stack,
  Paper,
  Button,
  Popover,
  Checkbox,
  TableRow,
  MenuItem,
  TableBody,
  TableCell,
  Container,
  Typography,
  // IconButton,
  Tooltip,
  TableContainer,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  LinearProgress,
} from '@mui/material';
import {
  useQuery,
  useMutation,
  useQueryClient,
} from 'react-query'
import { useDebounce } from 'use-debounce';
// components
import Label from '../components/label';
import Iconify from '../components/iconify';
import Scrollbar from '../components/scrollbar';
import { SnackbarContext } from '../components/snackbar/SnackbarContext';
// sections
import { MessageListHead, MessageListToolbar, Settings } from '../sections/@dashboard/send-imessage';
// hooks
import getMessages from '../hooks/queries/getMessages';
import importIMessages from '../hooks/mutations/importIMessages';
import exportIMessages from '../hooks/mutations/exportIMessages';
import deleteCampaignIMessages from '../hooks/mutations/deleteCampaignIMessages';
import retryCampaignIMessages from '../hooks/mutations/retryCampaignIMessages';
import getDevices from '../hooks/queries/getDevices';
// utils
import { fDateTime } from '../utils/formatTime';

// ----------------------------------------------------------------------

const TABLE_HEAD = [
  { id: 'recipient', label: 'Recipient', alignRight: false },
  { id: 'body', label: 'Content', alignRight: false },
  { id: 'status', label: 'Status', alignRight: false },
  { id: 'sendCount', label: 'Send Count', alignRight: false },
  { id: 'sendByDevice', label: 'Last Device', alignRight: false },
  { id: 'time', label: 'Last Updated', alignRight: false },
  { id: '' },
];

// ----------------------------------------------------------------------

const getMessageStatusLabel = (status) => {
  switch (status) {
    case 'pending':
      return <Label color="info">{sentenceCase(status)} ⏱</Label>;
    case 'sending':
      return <Label color="primary">{sentenceCase(status)} 🔵</Label>;
    case 'failed':
      return <Label color="error">{sentenceCase(status)} 🔴</Label>;
    case 'sent':
      return <Label color="success">{sentenceCase(status)} 🟢</Label>;
    case 'delivered':
      return <Label color="success">{sentenceCase(status)} 🟩</Label>;
    case 'read':
      return <Label color="success">{sentenceCase(status)} ✅</Label>;
    // case 'stop':
    //   return <Label color="error">Stopped</Label>;
    // case 'running':
    //   if (mode === 'check-imessage') {
    //     return <Label color="info">Check iMessage</Label>;
    //   }
    //   if (subStatus === 'rate-limited') {
    //     return <Label color="warning">Send iMessage (Rate Limited)</Label>;
    //   }
    //   return <Label color="success">Send iMessage</Label>;
    default:
      return <Label>{sentenceCase(status)}</Label>
  }
};

export default function SendIMessagePage() {
  const [open, setOpen] = useState(null);
  const [perPage, setPerPage] = useState(20);
  const [page, setPage] = useState(0);
  const [orderBy, setOrderBy] = useState('');
  const [orderDirection, setOrderDirection] = useState('asc');
  const [selectedItems, setSelectedItems] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const [filterStatus, setFilterStatus] = useState('total');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [isExporting, setIsExporting] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);
  const isMutating = isImporting || !!isExporting || isDeleting || isRetrying;

  const [searchValueDebounced] = useDebounce(searchValue, 500);

  const queryClient = useQueryClient();
  const { createNotification } = useContext(SnackbarContext);

  const { isLoading: isLoadingMessages, data: messagesResponse } = useQuery(['messages', 'list', {
    searchValue: searchValueDebounced,
    filterStatus,
    orderBy,
    orderDirection,
    perPage,
    page: page + 1,
  }], getMessages, {
    refetchInterval: 60000,
  });
  const { data: devices } = useQuery(['devices'], getDevices, {});

  const messages = messagesResponse?.messages ?? [];
  const dataCount = messages.length;
  const totalCount = messagesResponse?.pagination?.totalCount ?? 1;

  const importIMessagesMutation = useMutation(importIMessages, {
    onMutate: () => {
      setIsImporting(true);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['messages']);
      createNotification('success', 'Import file success', 3000);
    },
    onError: (error) => {
      createNotification('error', `Import file failed ${error?.message ?? ''}`, 5000);
    },
    onSettled: () => {
      setIsImporting(false);
    },
  });

  const exportIMessagesMutation = useMutation(exportIMessages, {
    onMutate: (exportType) => {
      setIsExporting(exportType ?? 'all');
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['messages']);
      createNotification('success', 'Export result success', 3000);
    },
    onError: (error) => {
      createNotification('error', `Export result failed ${error?.message ?? ''}`, 5000);
    },
    onSettled: () => {
      setIsExporting('');
    },
  });

  const deleteCampaignIMessagesMutation = useMutation(deleteCampaignIMessages, {
    onMutate: () => {
      setIsDeleting(true);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['messages']);
      createNotification('success', 'Delete messages success', 3000);
    },
    onError: (error) => {
      createNotification('error', `Delete messages failed ${error.message}`, 5000);
    },
    onSettled: () => {
      setIsDeleting(false);
    },
  });

  const retryCampaignIMessagesMutation = useMutation(retryCampaignIMessages, {
    onMutate: () => {
      setIsRetrying(true);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['messages']);
      createNotification('success', 'Retry messages success', 3000);
    },
    onError: (error) => {
      createNotification('error', `Retry messages failed ${error.message}`, 5000);
    },
    onSettled: () => {
      setIsRetrying(false);
    },
  });

  // const handleOpenMenu = (event) => {
  //   setOpen(event.currentTarget);
  // };

  const handleCloseMenu = () => {
    setOpen(null);
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && orderDirection === 'asc';
    setOrderDirection(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSelectAll = (event, data) => {
    if (event.target.checked) {
      const newSelecteds = (data ?? []).map((item) => item._id);
      setSelectedItems(newSelecteds);
      return;
    }
    setSelectedItems([]);
  };

  const handleSelectItem = (event, messageId) => {
    const selectedIndex = selectedItems.indexOf(messageId);
    let newSelected = [];
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selectedItems, messageId);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selectedItems.slice(1));
    } else if (selectedIndex === selectedItems.length - 1) {
      newSelected = newSelected.concat(selectedItems.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(selectedItems.slice(0, selectedIndex), selectedItems.slice(selectedIndex + 1));
    }
    setSelectedItems(newSelected);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangePerPage = (event) => {
    setPage(0);
    setPerPage(parseInt(event.target.value, 10));
  };

  const handleFilterByName = (event) => {
    setPage(0);
    setSearchValue(event.target.value);
  };

  const handleFilterStatus = (status) => {
    setPage(0);
    if (status === filterStatus) {
      if (status !== 'total') {
        setFilterStatus('total');
      }
    } else {
      setFilterStatus(status);
    }
  };

  const handleImportIMessages = async (event) => {
    const file = event.target.files[0];

    importIMessagesMutation.mutate(file);
  };

  const handleExportIMessages = (exportType) => {
    exportIMessagesMutation.mutate(exportType);
  }

  const handleDeleteMessages = (event) => {
    setDeleteDialogOpen(false);
    deleteCampaignIMessagesMutation.mutate();
  }

  const handleRetryMessages = (event) => {
    retryCampaignIMessagesMutation.mutate();
  }

  const isNotFound = !!searchValue && !dataCount;

  return (
    <>
      <Helmet>
        <title>SpamImess - Send iMessages</title>
      </Helmet>

      <Container maxWidth={false} sx={{ minWidth: '1600px' }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
          <Typography variant="h4" gutterBottom>
            Send iMessages
          </Typography>
          <Stack direction="row" alignItems="center" gap={1}>
            <Button variant="contained" color="info" startIcon={<Iconify icon="eva:archive-outline" />} href="/uploads/ImportSendImessageTemplate.xlsx" target="_blank">Template file</Button>
            <Button variant="contained" color="primary" startIcon={isImporting ? <CircularProgress size="20px" /> : <Iconify icon="eva:upload-outline" />} component="label" disabled={isMutating}>
              <CircularProgress size="sm" /> Import
              <input type="file" hidden onChange={handleImportIMessages} disabled={isMutating} onClick={(event) => { event.target.value = null; }} />
            </Button>
            <Button variant="contained" color="info" startIcon={isExporting === 'all' ? <CircularProgress size="20px" /> : <Iconify icon="eva:download-outline" />} disabled={isMutating} onClick={() => { handleExportIMessages(); }}>
              <CircularProgress size="sm" />Export All
            </Button>
            <Button variant="contained" color="success" startIcon={isExporting === 'success' ? <CircularProgress size="20px" /> : <Iconify icon="eva:checkmark-circle-outline" />} disabled={isMutating} onClick={() => { handleExportIMessages('success'); }}>
              Export Success
            </Button>
            <Button variant="contained" color="warning" startIcon={isExporting === 'failed' ? <CircularProgress size="20px" /> : <Iconify icon="eva:alert-circle-outline" />} disabled={isMutating} onClick={() => { handleExportIMessages('failed'); }}>
              Export Failed
            </Button>
            <Button variant="contained" color="error" startIcon={isDeleting ? <CircularProgress size="20px" /> : <Iconify icon="eva:trash-2-outline" />} disabled={isMutating} onClick={() => { setDeleteDialogOpen(true); }}>
              Delete All
            </Button>
            <Dialog
              open={deleteDialogOpen}
              onClose={() => { setDeleteDialogOpen(false); }}
              aria-labelledby="alert-dialog-title"
              aria-describedby="alert-dialog-description"
            >
              <DialogTitle id="alert-dialog-title">
                Are you sure want to delete all messages?
              </DialogTitle>
              <DialogContent>
                <DialogContentText id="alert-dialog-description">
                  This operation is not revertable!
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button color="info" onClick={() => { setDeleteDialogOpen(false); }} autoFocus>Cancel</Button>
                <Button color="error" onClick={handleDeleteMessages}>Delete All</Button>
              </DialogActions>
            </Dialog>
          </Stack>
        </Stack>

        <Stack mb={2}>
          <Settings />
        </Stack>

        <Card>
          <MessageListToolbar isMutating={isMutating} isRetrying={isRetrying} numSelected={selectedItems.length} filterName={searchValue} onFilterName={handleFilterByName} filterStatus={filterStatus} onFilterStatus={handleFilterStatus} onRetryMessages={handleRetryMessages} />

          {/* <Box display="flex" mb={2} pr={2} justifyContent="flex-end">
            <Button variant="contained" color='warning' startIcon={isRetrying ? <CircularProgress size="20px" /> : <Iconify icon="eva:flip-2-outline" />} disabled={isMutating} onClick={handleRetryMessages}>Retry Pending/Failed Messages</Button>
          </Box> */}

          <Scrollbar>
            <TableContainer sx={{ minWidth: 800 }}>
              <Table>
                <MessageListHead
                  order={orderDirection}
                  orderBy={orderBy}
                  headLabel={TABLE_HEAD}
                  rowCount={dataCount}
                  numSelected={selectedItems.length}
                  onRequestSort={handleRequestSort}
                  onSelectAllClick={(event) => handleSelectAll(event, messages)}
                />
                <TableBody>
                  {!isLoadingMessages && !!dataCount && messages.map((row) => {
                    const { _id: messageId, recipient, type, body, status, reason, sendCount, sendByDevice, latestMessageStatusTime } = row;
                    const isSelected = selectedItems.indexOf(messageId) !== -1;
                    const statusTooltip = `${reason ? `Reason: ${reason}` : ''}`;
                    const device = (devices ?? []).find((device) => sendByDevice && sendByDevice === device._id);

                    return (
                      <TableRow hover key={messageId} tabIndex={-1} role="checkbox" selected={isSelected}>
                        <TableCell padding="checkbox">
                          <Checkbox checked={isSelected} onChange={(event) => handleSelectItem(event, messageId)} />
                        </TableCell>

                        <TableCell component="th" scope="row" padding="none">
                          <Typography variant="subtitle2" noWrap>
                            {recipient}
                          </Typography>
                        </TableCell>

                        <TableCell align="left">{body}</TableCell>

                        <TableCell align="left">
                          <Tooltip title={statusTooltip}>{getMessageStatusLabel(status)}</Tooltip>
                        </TableCell>

                        <TableCell align="left">{sendCount}</TableCell>

                        <TableCell align="left">{device?.name || device?.serialNumber || ''}</TableCell>

                        <TableCell align="left">{status !== 'pending' ? fDateTime(latestMessageStatusTime) : ''}</TableCell>

                        <TableCell align="right">
                          {/* <IconButton size="large" color="inherit" onClick={handleOpenMenu}>
                            <Iconify icon={'eva:more-vertical-fill'} />
                          </IconButton> */}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {isLoadingMessages && (
                    <TableRow style={{ height: 53 * perPage }}>
                      <TableCell colSpan={8}>
                        <Box sx={{ width: '100%' }}>
                          <LinearProgress />
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>

                {isNotFound && (
                  <TableBody>
                    <TableRow>
                      <TableCell align="center" colSpan={8} sx={{ py: 3 }}>
                        <Paper
                          sx={{
                            textAlign: 'center',
                          }}
                        >
                          <Typography variant="h6" paragraph>
                            Not found
                          </Typography>

                          <Typography variant="body2">
                            No results found for &nbsp;
                            <strong>&quot;{searchValue}&quot;</strong>.
                            <br /> Try checking for typos or using complete words.
                          </Typography>
                        </Paper>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                )}
              </Table>
            </TableContainer>
          </Scrollbar>

          {!isLoadingMessages && <TablePagination
            rowsPerPageOptions={[20, 50, 100]}
            component="div"
            count={totalCount}
            rowsPerPage={perPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangePerPage}
          />}
        </Card>
      </Container>

      <Popover
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            p: 1,
            width: 140,
            '& .MuiMenuItem-root': {
              px: 1,
              typography: 'body2',
              borderRadius: 0.75,
            },
          },
        }}
      >
        <MenuItem>
          <Iconify icon={'eva:edit-fill'} sx={{ mr: 2 }} />
          Edit
        </MenuItem>

        <MenuItem sx={{ color: 'error.main' }}>
          <Iconify icon={'eva:trash-2-outline'} sx={{ mr: 2 }} />
          Delete
        </MenuItem>
      </Popover>
    </>
  );
}
